/**
 * Trunnion AI - Token Usage Meter
 * Bar chart displaying token usage per step.
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { ExecutionStep } from '@/types';

interface TokenUsageMeterProps {
  steps: ExecutionStep[];
}

export function TokenUsageMeter({ steps }: TokenUsageMeterProps) {
  const data = steps
    .filter((s) => s.tokenUsage && s.tokenUsage.totalTokens > 0)
    .map((step) => ({
      name: step.nodeName.slice(0, 12),
      prompt: step.tokenUsage.promptTokens,
      completion: step.tokenUsage.completionTokens,
      total: step.tokenUsage.totalTokens,
      cost: step.tokenUsage.estimatedCost,
    }));

  const totalTokens = data.reduce((sum, d) => sum + d.total, 0);
  const totalCost = data.reduce((sum, d) => sum + d.cost, 0);

  if (data.length === 0) {
    return (
      <div className="text-center py-6 text-sm text-trunnion-slate-400">
        No token usage data available
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-white">Token Usage</h4>
        <div className="flex items-center gap-4 text-xs text-trunnion-slate-400">
          <span>{totalTokens.toLocaleString()} tokens</span>
          <span>${totalCost.toFixed(4)}</span>
        </div>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} barGap={2}>
            <XAxis
              dataKey="name"
              tick={{ fill: '#8D97A6', fontSize: 10 }}
              axisLine={{ stroke: '#374151' }}
            />
            <YAxis
              tick={{ fill: '#8D97A6', fontSize: 10 }}
              axisLine={{ stroke: '#374151' }}
              width={50}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1A202C',
                border: '1px solid #374151',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              labelStyle={{ color: '#FFFFFF' }}
            />
            <Bar dataKey="prompt" stackId="tokens" name="Prompt" radius={[0, 0, 0, 0]}>
              {data.map((_, i) => (
                <Cell key={i} fill="#3B82F6" />
              ))}
            </Bar>
            <Bar dataKey="completion" stackId="tokens" name="Completion" radius={[4, 4, 0, 0]}>
              {data.map((_, i) => (
                <Cell key={i} fill="#D4A017" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-4 mt-2 justify-center">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-blue-500" />
          <span className="text-xs text-trunnion-slate-400">Prompt</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-trunnion-gold-500" />
          <span className="text-xs text-trunnion-slate-400">Completion</span>
        </div>
      </div>
    </div>
  );
}
